/**
 * SealedScreen — elenco delle conversazioni ANONIME (sealed-sender, v2.2).
 *
 * Superficie separata dalle chat normali: qui il server non sa chi-con-chi.
 * Poll periodico (receiveSealed) per scoprire nuovi messaggi/mittenti.
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Frame, Header, Footer, Btn, Box } from '../components/Frame';
import { useStore } from '../lib/store';
import { receiveSealed } from '../lib/sealed';
import { listSealedConvos, getSealedTTL, setSealedTTL } from '../lib/sealedStore';

const TTL_OPTS = [
  { ms: 3600000, label: '1h' },
  { ms: 86400000, label: '24h' },
  { ms: 604800000, label: '7g' },
  { ms: 0, label: 'mai' },
];

export default function SealedScreen() {
  const navigate = useNavigate();
  const { boxPub, boxSec } = useStore();
  const [convos, setConvos] = useState([]);
  const [newSigil, setNewSigil] = useState('');
  const [ttl, setTtl] = useState(getSealedTTL());

  const refresh = useCallback(async () => {
    try { await receiveSealed({ boxKeypair: { pub: boxPub, priv: boxSec } }); } catch { /* poll */ }
    setConvos(await listSealedConvos());
  }, [boxPub, boxSec]);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 6000);
    return () => clearInterval(t);
  }, [refresh]);

  function startNew() {
    const s = newSigil.trim().toLowerCase().replace(/^@/, '');
    if (/^[a-z0-9_]{3,20}$/.test(s)) navigate(`/sealed-chat/${s}`);
  }

  return (
    <Frame statusLeft="ANONIMO :: SEALED" statusRight="🧅 5G">
      <Header title="// Anonimo" crumb="◀ inbox" onCrumbClick={() => navigate('/inbox')} />

      <Box className="text-[13px] text-ink-dim leading-relaxed mb-3">
        <p className="text-amber mb-1">🛡 Messaggi a mittente nascosto</p>
        Il messaggio resta protetto, <b className="text-ink">ma qui il server non sa chi lo manda</b>.
        Niente conferme di lettura né notifiche. Per il massimo, usalo da un'<b className="text-ink">identità Tor</b>.
      </Box>

      {/* Effimero: i messaggi anonimi spariscono da questo device dopo… */}
      <div className="flex items-center flex-wrap gap-1.5 mb-4 text-[13px]">
        <span className="text-ink-dim mr-1">⏳ Spariscono dopo:</span>
        {TTL_OPTS.map((o) => (
          <button
            key={o.ms}
            onClick={() => { setSealedTTL(o.ms); setTtl(o.ms); }}
            className={`px-2 py-1 rounded-md border text-[12px] transition-colors ${ttl === o.ms ? 'border-amber text-amber bg-amber/10' : 'border-ink-dim/50 text-ink-dim hover:text-ink'}`}
          >
            {o.label}
          </button>
        ))}
      </div>

      <div className="flex gap-2 mb-4">
        <input
          value={newSigil}
          onChange={(e) => setNewSigil(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') startNew(); }}
          placeholder="@sigil destinatario"
          autoCapitalize="none"
          autoComplete="off"
          spellCheck="false"
          className="flex-1 min-w-0 bg-black border border-ink-dim text-ink font-mono text-[15px] px-3 py-2 rounded-lg focus:border-amber focus:outline-none"
        />
        <button
          onClick={startNew}
          disabled={!newSigil.trim()}
          className="shrink-0 border border-amber rounded-lg bg-amber/10 text-amber text-[14px] px-3 py-2 hover:bg-amber/20 transition-colors disabled:opacity-40"
        >
          ✚ Scrivi
        </button>
      </div>

      <div className="space-y-2">
        {convos.length === 0 && (
          <p className="text-[13px] text-ink-dim text-center py-6">Ancora nessuna chat anonima. Scrivi a un @sigil per iniziare.</p>
        )}
        {convos.map((c) => {
          const last = c.messages[c.messages.length - 1];
          return (
            <button
              key={c.peerSigil.toLowerCase()}
              onClick={() => navigate(`/sealed-chat/${c.peerSigil.toLowerCase()}`)}
              className="w-full flex flex-col items-start gap-0.5 border border-ink-dim/50 rounded-xl bg-ink/[0.02] px-4 py-3 text-left hover:bg-ink/[0.07] transition-colors"
            >
              <span className="font-mono text-[16px] text-amber">@{c.peerSigil}</span>
              {last && <span className="text-[13px] text-ink-dim truncate w-full">{last.mine ? '→ ' : ''}{last.text}</span>}
            </button>
          );
        })}
      </div>

      <Footer>
        <Btn variant="ghost" onClick={() => navigate('/inbox')}>◀ Inbox</Btn>
      </Footer>
    </Frame>
  );
}
